import React, { useState, useEffect } from 'react';
import { Plus, X, Loader2 } from 'lucide-react';
import TagInput from './TagInput';
import { useAuth } from '../../context/AuthContext';

const ObjectTags = ({ objectType, objectId, onTagsChanged }) => {
    const { authFetch } = useAuth();
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInput, setShowInput] = useState(false);

    useEffect(() => {
        if (!objectType || !objectId) return;
        
        const fetchTags = async () => {
            try {
                const res = await authFetch(`/api/tags/object/${objectType}/${objectId}`);
                if (res.ok) {
                    const data = await res.json();
                    setTags(data);
                }
            } catch (error) {
                console.error("Error fetching object tags:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTags();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectType, objectId]);

    const handleAddTag = async (tagName) => {
        setShowInput(false);
        // Optimistically update is risky if we don't have the color, but we can wait
        setLoading(true);
        try {
            const res = await authFetch(`/api/tags/assign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tag_name: tagName, object_type: objectType, object_id: objectId })
            });
            if (res.ok) {
                const data = await res.json();
                // Avoid duplicates
                if (!tags.find(t => t.id === data.tag.id)) {
                    const newTags = [...tags, data.tag];
                    setTags(newTags);
                    if (onTagsChanged) onTagsChanged(newTags);
                    window.dispatchEvent(new CustomEvent('objectTagsChanged', { detail: { objectType, objectId } }));
                }
            }
        } catch (error) {
            console.error("Error assigning tag:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveTag = async (tagId) => {
        try {
            const res = await authFetch(`/api/tags/assign`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tag_id: tagId, object_type: objectType, object_id: objectId })
            });
            if (res.ok) {
                const newTags = tags.filter(t => t.id !== tagId);
                setTags(newTags);
                if (onTagsChanged) onTagsChanged(newTags);
                window.dispatchEvent(new CustomEvent('objectTagsChanged', { detail: { objectType, objectId } }));
            }
        } catch (error) {
            console.error("Error removing tag:", error);
        }
    };

    if (loading && tags.length === 0) {
        return <div className="flex items-center text-gray-500"><Loader2 size={12} className="animate-spin mr-1" /></div>;
    }

    return (
        <div className="flex items-center gap-1.5 relative w-full">
            {/* Tags container */}
            <div className="flex items-center flex-wrap gap-1">
                {tags.map(tag => (
                    <div 
                        key={tag.id}
                        className="flex items-center text-[11px] px-1.5 py-0.5 rounded-md font-medium text-white shadow-sm transition-opacity hover:opacity-90 cursor-default group"
                        style={{ backgroundColor: tag.color }}
                    >
                        <span className="mr-1">{tag.name}</span>
                        <button 
                            onClick={() => handleRemoveTag(tag.id)}
                            className="opacity-60 hover:opacity-100 focus:outline-none bg-black/20 rounded-full p-0.5"
                        >
                            <X size={8} />
                        </button>
                    </div>
                ))}
            </div>
            
            {/* Add button - always visible on the right */}
            <div className="relative flex-shrink-0 ml-auto">
                <button 
                    onClick={() => setShowInput(!showInput)}
                    className="flex items-center justify-center w-5 h-5 rounded-md bg-[#374151] hover:bg-[#4b5563] text-gray-300 transition-colors"
                    title="Añadir etiqueta"
                >
                    <Plus size={12} />
                </button>
                
                {showInput && (
                    <TagInput 
                        onSelect={handleAddTag} 
                        onClose={() => setShowInput(false)} 
                    />
                )}
            </div>
        </div>
    );
};

export default ObjectTags;
